import { useState, useCallback } from 'react';

export const useHtmlEditor = () => {
  const [editorMode, setEditorMode] = useState('rich'); // 'rich' or 'html'
  const [richTextContent, setRichTextContent] = useState('');
  const [htmlContent, setHtmlContent] = useState('');

  // Format HTML with proper indentation for readability
  const formatHtml = useCallback((html) => {
    let formatted = '';
    let indent = 0;
    const tags = html.match(/<\/?[^>]*>/g) || [];
    let lastIndex = 0;

    tags.forEach(tag => {
      const tagStart = html.indexOf(tag, lastIndex);
      const textBefore = html.substring(lastIndex, tagStart).trim();

      if (textBefore) {
        formatted += '  '.repeat(indent) + textBefore + '\n';
      }

      if (tag.startsWith('</')) {
        indent = Math.max(0, indent - 1);
      }

      formatted += '  '.repeat(indent) + tag + '\n';

      if (!tag.startsWith('</') && !tag.endsWith('/>') && !['br', 'hr', 'img', 'input', 'meta', 'link'].includes(tag.slice(1, -1).split(/\s/)[0])) {
        indent++;
      }

      lastIndex = tagStart + tag.length;
    });

    const textAfter = html.substring(lastIndex).trim();
    if (textAfter) {
      formatted += '  '.repeat(indent) + textAfter + '\n';
    }

    return formatted.trim();
  }, []);

  // Handle editor mode switching with content conversion
  const handleModeSwitch = useCallback((newMode) => {
    if (newMode === editorMode) return; // No change needed

    if (newMode === 'rich' && htmlContent.trim()) {
      // Switching to rich text mode - load HTML content into rich editor
      setRichTextContent(htmlContent);
    } else if (newMode === 'html' && richTextContent.trim()) {
      // Switching to HTML mode - get HTML from rich text editor and format it
      setHtmlContent(formatHtml(richTextContent));
    }

    setEditorMode(newMode);
  }, [editorMode, htmlContent, richTextContent, formatHtml]);

  // Get the active content based on current mode
  const getActiveContent = useCallback(() => {
    return editorMode === 'rich' ? richTextContent : htmlContent;
  }, [editorMode, richTextContent, htmlContent]);

  // Initialize content based on body text
  const initializeContent = useCallback((body) => {
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(body);

    if (hasHtmlTags) {
      // Content appears to be HTML, put it in HTML mode
      setHtmlContent(body);
      setRichTextContent('');
      setEditorMode('html');
    } else {
      // Plain text or rich text content
      setRichTextContent(body);
      setHtmlContent('');
      setEditorMode('rich');
    }
  }, []);

  // Reset all content
  const resetContent = useCallback(() => {
    setRichTextContent('');
    setHtmlContent('');
    setEditorMode('rich');
  }, []);

  return {
    editorMode,
    richTextContent,
    htmlContent,
    setEditorMode,
    setRichTextContent,
    setHtmlContent,
    formatHtml,
    handleModeSwitch,
    getActiveContent,
    initializeContent,
    resetContent,
  };
};
